import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Button,
  Avatar,
  Paper,
  Alert,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { formatDistanceToNow } from "date-fns";
import API_BASE_URL from "../../utils/api";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";

const GuestThreadPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [thread, setThread] = useState(null);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");

  const fetchThread = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/threads/guest/${id}?token=${token}`
      );
      setThread(res.data);
    } catch (err) {
      setError(err.response?.data?.error || "Unable to load this thread.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThread();
  }, [id, token]);

  const submitGuestComment = async () => {
    if (!comment.trim()) return;

    try {
      setPosting(true);

      await axios.post(`${API_BASE_URL}/threads/guest/${id}/comment`, {
        token,
        content: comment,
      });

      setComment("");
      fetchThread();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to post comment.");
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !thread) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const postedBy = thread.guestName || "Guest User";

  return (
    <Paper
      elevation={0}
      sx={{
        width: "auto",
        padding: { xs: "18px", sm: "26px", md: "28px 70px" },
        margin: { xs: "12px auto", md: "20px auto" },
        borderRadius: "16px",
        backgroundColor: "#ffffff",
        border: "1px solid #eee",
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          mb: 2,
          pb: 2,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Avatar sx={{ width: 42, height: 42 }}>{postedBy.charAt(0)}</Avatar>

        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              {postedBy}
            </Typography>

            <Box
              sx={{
                backgroundColor: "#fff3cd",
                color: "#856404",
                px: 1,
                py: 0.2,
                borderRadius: "999px",
                fontSize: "0.7rem",
                fontWeight: 700,
              }}
            >
              Guest
            </Box>
          </Box>

          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {formatDistanceToNow(new Date(thread.createdAt), {
              addSuffix: true,
            })}
          </Typography>
        </Box>
      </Box>

      <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2, mb: 2 }}>
        {thread.title}
      </Typography>

      <Box
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: "1px solid #eee",
          backgroundColor: "#fff",
          mb: 3,
          color: "#2b2b2b",
          fontSize: "1rem",
          "& *": { lineHeight: 1.85 },
          "& p": { marginBottom: "12px" },
          "& img": { maxWidth: "100%", borderRadius: "12px" },
          "& a": { color: "#1a73e8", fontWeight: 600 },
        }}
        dangerouslySetInnerHTML={{ __html: thread.content }}
      />

      {thread.attachments?.map((file, index) => (
        <Paper
          key={index}
          elevation={0}
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            border: "1px solid #eee",
            backgroundColor: "#fafafa",
          }}
        >
          {file.type === "image" ? (
            <Box
              component="img"
              src={file.url}
              alt="attachment"
              sx={{
                width: "100%",
                maxHeight: 340,
                objectFit: "cover",
                borderRadius: 2,
              }}
            />
          ) : (
            <Button href={file.url} target="_blank" variant="outlined">
              View PDF
            </Button>
          )}
        </Paper>
      ))}

      <Box
        sx={{
          mt: 4,
          borderRadius: 3,
          border: "1px solid #eee",
          overflow: "hidden",
          backgroundColor: "#fafafa",
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            display: "flex",
            alignItems: "center",
            gap: 1,
            backgroundColor: "#f6f6f6",
            borderBottom: "1px solid #eee",
          }}
        >
          <ChatBubbleOutlineIcon sx={{ color: "#666" }} />
          <Typography sx={{ fontWeight: 900, fontSize: "1.05rem" }}>
            Comments
          </Typography>
        </Box>

        <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
          {thread.comments?.length ? (
            thread.comments.map((c) => (
              <Paper key={c._id} sx={{ p: 2, mb: 2 }}>
                <Typography sx={{ fontWeight: 700 }}>
                  {c.isGuestComment
                    ? c.guestName || "Guest User"
                    : c.user?.name || "Registered User"}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  {c.createdAt
                    ? formatDistanceToNow(new Date(c.createdAt), {
                        addSuffix: true,
                      })
                    : ""}
                </Typography>

                <Typography sx={{ mt: 1 }}>{c.comment}</Typography>
              </Paper>
            ))
          ) : (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              No comments yet.
            </Typography>
          )}

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Write a reply"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            sx={{ mt: 2 }}
          />

          <Button
            variant="contained"
            onClick={submitGuestComment}
            disabled={posting || !comment.trim()}
            sx={{ mt: 2, textTransform: "none", fontWeight: 700 }}
          >
            {posting ? "Posting..." : "Post Comment"}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default GuestThreadPage;
